import { Link } from "react-router-dom";
import type { Tip } from "@/types/content";
import { categories } from "@/mocks/categories";

const statusMap: Record<string, { label: string; cls: string; icon: string }> = {
  hot: { label: "热议中", cls: "bg-primary-500 text-background-50", icon: "ri-fire-fill" },
  new: { label: "新爆料", cls: "bg-accent-500 text-foreground-950", icon: "ri-sparkling-2-fill" },
  verified: { label: "已核实线索", cls: "bg-secondary-700 text-background-50", icon: "ri-shield-check-fill" },
};

interface TipCardProps {
  tip: Tip;
  layout?: "vertical" | "horizontal";
}

export default function TipCard({ tip, layout = "vertical" }: TipCardProps) {
  const category = categories.find((item) => item.slug === tip.category);
  const status = statusMap[tip.status] ?? statusMap.hot;

  if (layout === "horizontal") {
    return (
      <article className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300">
        <div className="hidden h-24 w-40 shrink-0 overflow-hidden rounded-md bg-background-200 sm:block">
          <img
            src={tip.cover}
            alt={`${tip.title} 爆料配图`}
            title={`${tip.title} - 爆料专区封面`}
            className="h-full w-full object-cover object-top"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 font-label text-xs text-secondary-900">
              {category?.label}
            </span>
            <span className="font-label text-xs text-foreground-500">{tip.createdAt}</span>
          </div>
          <h3 className="mb-1 text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
            <Link to={`/detail/${tip.id}`}>{tip.title}</Link>
          </h3>
          <p className="line-clamp-2 text-sm leading-relaxed text-foreground-600">{tip.summary}</p>
          <div className="mt-auto flex items-center gap-4 pt-3 font-label text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-user-3-line" aria-hidden="true" />
              {tip.author}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-heart-3-line" aria-hidden="true" />
              {tip.likes}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <div className="relative h-48 w-full overflow-hidden bg-background-200">
        <img
          src={tip.cover}
          alt={`${tip.title} 爆料配图`}
          title={`${tip.title} - 爆料专区封面`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span
          className={`absolute left-3 top-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-label text-xs font-medium ${status.cls}`}
        >
          <i className={status.icon} aria-hidden="true" />
          {status.label}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2 font-label text-xs">
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-secondary-900">
            {category?.label}
          </span>
          <span className="text-foreground-500">{tip.location}</span>
        </div>
        <h3 className="mb-2 text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
          <Link to={`/detail/${tip.id}`}>{tip.title}</Link>
        </h3>
        <p className="line-clamp-3 text-sm leading-relaxed text-foreground-600">{tip.summary}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 font-label text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-user-3-line" aria-hidden="true" />
            {tip.author}
          </span>
          <span className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true" />
              {tip.views}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-heart-3-line" aria-hidden="true" />
              {tip.likes}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}